import { useWorkspaceText } from '../interface-copy';
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import appPageCopy from '@/i18n/ns/app-page';
import { useNarrowViewport } from '../../lib/mobile';
import { useImageViewer } from '../../../src/apps/runtime/kit/index.ts';
import type { AppSdk } from '../../../src/apps/runtime/tier0/index.mjs';
import { DataTab, type DataTabProps } from './data/data-tab';
import { HistoryTab, type HistoryTabProps } from './history/history-tab';
import { useManagedImageRenderer, viewerEntries } from './media/managed-image';
import {
  dataImageOwner,
  historyImageOwner,
  rowHistoryImageOwner,
  type ManagedImageData,
} from './media/media-model';
import { AppPage, type AppPageProps } from './page/app-page';
import { activeTab } from './page/page-model';
import { InlineError } from '../primitives';
import { SavedVersionsSlotContext } from './saved-versions-slot';
import { VersionsTab, type VersionsTabProps } from './versions/versions-tab';

export type AppSurfaceProps = {
  /** The page: identity, versions, rights, failure and handlers. The tab is owned here. */
  page: Omit<AppPageProps, 'tab' | 'onTab' | 'slots'> | null;
  /** RUN's App frame, the App tab's content. */
  appFrame: ReactNode;
  /** Each portion is the host's projection, or null when it is not available (said so, never faked). */
  data: Omit<DataTabProps, 'renderImage'> | null;
  history: Omit<HistoryTabProps, 'renderImage'> | null;
  versions: VersionsTabProps | null;
  /** Native completed C9 history and one-step restore, supplied by its actual reader. */
  completedVersions?: ReactNode;
  /** The selected version's budget and schedule controls (owners and editors), shown in the Versions tab. */
  versionsControls?: ReactNode;
  /** Trusted host modals over the selected App page. */
  hostOverlays?: ReactNode;
  /** The same SDK/document lifetime as the images: invalidation closes the viewer. */
  lifetime: Pick<AppSdk, 'onInvalidate'>;
  /** Inspect on: a click selects, never zooms. */
  inspectOn?: boolean;
  initialTab?: AppPageProps['tab'];
  /**
   * A request to show `initialTab` NOW, also when this App is already open on another tab: each new token
   * (an object identity) re-selects it. Absent, the tab is only read at mount.
   */
  revealToken?: unknown;
  /** Re-ask the host for a portion it could not supply; absent, no Try again is offered (nothing would happen). */
  retryPortion?: (portion: 'data' | 'history' | 'versions') => void;
  /** Tab changes, for the caller's route/URL. */
  onTabChange?: (tab: AppPageProps['tab']) => void;
  /** The App's own page: its actions sit in the page's top bar. */
  actionsInBar?: boolean;
};

/**
 * Every image the Data and History tabs draw, each with the place it is drawn. The viewer opens by
 * place + ART key, so one key shown in two places stays two items; duplicates are never merged or
 * resolved to the first (the shared viewer refuses an ambiguous key).
 */
export function surfaceImages(
  data: Pick<DataTabProps, 'rows' | 'history'> | null,
  history: Pick<HistoryTabProps, 'events'> | null,
): { owner: string; data: ManagedImageData }[] {
  const found: { owner: string; data: ManagedImageData }[] = [];
  for (const [tableId, state] of Object.entries(data?.rows ?? {}))
    if (state.status === 'ready')
      for (const row of state.rows)
        for (const [columnKey, image] of Object.entries(row.images ?? {}))
          found.push({ owner: dataImageOwner(tableId, row.id, columnKey), data: image });
  if (data?.history?.status === 'ready')
    for (const change of data.history.changes)
      if (change.kind === 'changed' && change.image)
        found.push({ owner: rowHistoryImageOwner(change), data: change.image });
  for (const event of history?.events ?? [])
    if (event.kind === 'entry' && event.image)
      found.push({ owner: historyImageOwner(event.id), data: event.image });
  return found;
}

/**
 * The App surface in the workspace: APP's page shell with its Data, History and Versions tabs over
 * the existing components. It owns only the tab and the one shared image viewer; every value, right
 * and handler comes from the host (ART's App VM, APP/DATA/PUB, BO), never a second view model.
 */
export function AppSurface(props: AppSurfaceProps) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(appPageCopy);
  const [tab, setTab] = useState<AppPageProps['tab']>(props.initialTab ?? 'app');
  const revealed = useRef<unknown>(props.revealToken);
  useLayoutEffect(() => {
    if (props.revealToken === undefined || revealed.current === props.revealToken) return;
    revealed.current = props.revealToken;
    if (props.initialTab) setTab(props.initialTab);
  });
  // A tab the person can no longer see is not remembered: a later restore of the right does not revive it.
  const visible = props.page ? activeTab(tab, props.page.rights, { deck: props.page.deck }) : 'app';
  useLayoutEffect(() => {
    if (visible === tab) return;
    setTab(visible);
    props.onTabChange?.(visible);
  });
  // A host without Versions data borrows the saved canvas's own reader, switched on only while the tab is open.
  const savedVersions = useContext(SavedVersionsSlotContext);
  const borrowVersions =
    !props.versions && props.completedVersions === undefined && savedVersions !== null;
  const wantVersions = savedVersions?.wanted;
  const versionsOpen = visible === 'versions';
  useEffect(() => {
    if (!borrowVersions || !wantVersions) return;
    wantVersions(versionsOpen);
    return () => wantVersions(false);
  }, [borrowVersions, wantVersions, versionsOpen]);
  const images = useMemo(
    () => surfaceImages(props.data, props.history),
    [props.data, props.history],
  );
  const entries = useMemo(() => viewerEntries(images), [images]);
  const viewer = useImageViewer({
    lifetime: props.lifetime,
    images: entries,
    inspectOn: props.inspectOn === true,
    // DES2: on a phone the viewer is a full-screen surface, not a scrim over the page.
    phone: useNarrowViewport(),
  });
  // What the viewer was opened on, plus every image that became navigable since: every item's projection (image, ratio and the changed-since note) as
  // the viewer validated it. The viewer is selected by key and re-reads its items each render, so if any
  // recorded place now holds a different projection (or none, or is ambiguous) the viewer is withheld in
  // THAT commit and closed in its layout effect, never shown as the original for a frame.
  const opened = useRef<Map<string, string> | null>(null);
  const latest = useRef(entries);
  latest.current = entries;
  const signatures = (list: typeof entries) => {
    const seen = new Map<string, string>();
    for (const entry of list)
      seen.set(
        entry.key,
        seen.has(entry.key)
          ? 'ambiguous'
          : JSON.stringify([entry.image, entry.ratio, entry.changedSince === true]),
      );
    return seen;
  };
  const was = opened.current;
  const replaced = (() => {
    if (!was) return false;
    const now = signatures(entries);
    return [...was].some(([key, sig]) => now.get(key) !== sig);
  })();
  useLayoutEffect(() => {
    if (replaced) {
      opened.current = null;
      viewer.close();
    } else if (opened.current) {
      // An image that became navigable after the open is fenced from the commit it first appeared in.
      for (const [key, sig] of signatures(entries))
        if (!opened.current.has(key)) opened.current.set(key, sig);
    }
  });
  const renderImage = useManagedImageRenderer((key, origin) => {
    opened.current = signatures(latest.current);
    viewer.open(key, origin);
  }, props.inspectOn === true);
  return (
    <>
      <AppPage
        {...(props.page ?? { pending: true as const })}
        actionsInBar={props.actionsInBar}
        tab={tab}
        onTab={(next) => {
          setTab(next);
          props.onTabChange?.(next);
        }}
        slots={{
          app: props.appFrame,
          data: props.data ? (
            <DataTab {...props.data} renderImage={renderImage} />
          ) : (
            <PortionUnavailable text={copy.portionUnavailable} />
          ),
          history: props.history ? (
            <HistoryTab {...props.history} renderImage={renderImage} />
          ) : (
            <InlineError
              testId="portion-unavailable"
              message={uiText('We couldn’t load what happened in this App. Try again in a moment.')}
              onRetry={props.retryPortion ? () => props.retryPortion?.('history') : undefined}
            />
          ),
          versions: (
            <>
              {props.completedVersions !== undefined ? (
                props.completedVersions
              ) : props.versions ? (
                <VersionsTab {...props.versions} />
              ) : savedVersions && borrowVersions ? (
                savedVersions.panel
              ) : (
                <PortionUnavailable text={copy.portionUnavailable} />
              )}
              {props.versionsControls}
            </>
          ),
        }}
      />
      {replaced ? null : viewer.viewer}
      {props.hostOverlays}
    </>
  );
}

function PortionUnavailable({ text }: { text: string }) {
  return (
    <p role="status" data-testid="portion-unavailable">
      {text}
    </p>
  );
}
